import type { LoaderFunctionArgs } from 'react-router';

import { data, Route, Routes, matchPath, useLoaderData } from 'react-router';

import { useSession } from 'src/hooks/useSession';

import { Auth } from 'src/screens/Auth';
import { Plans } from 'src/screens/Plans';
import { Deals } from 'src/screens/Deals';
import { Rental } from 'src/screens/Rental';
import { Profile } from 'src/screens/Profile';
import { Register } from 'src/screens/Register';
import { NotFound } from 'src/screens/NotFound';
import { DeliveryDeal } from 'src/screens/DeliveryDeal';
import { Notifications } from 'src/screens/Notifications';
import { XpTransactions } from 'src/screens/XpTransactions';

/**
 * The screens a session opens — the browser's to render: the session lives in its storage, and
 * the server knows nothing of it. The route table below is the one `App` always had, less the
 * pages the server renders (`src/routes.ts`).
 */
export const CLIENT_SCREEN_PATHS = [
  '/plans',
  '/register',
  '/deals',
  '/delivery/:id',
  '/rental/:id',
  '/profile',
  '/notifications',
  '/xp-transactions',
  '/auth',
  '/auth-modal',
];

/** What the server says of an address under this route: nothing, or that nothing is there. */
export interface NothingHere {
  notFound: true;
}

/**
 * The server renders none of these screens, but it does answer for the address: one of the
 * screens above gets the splash and a 200, anything else is a 404 with the not-found screen —
 * a mistyped address used to answer 200 like every other.
 */
export function loader({ request }: LoaderFunctionArgs) {
  const { pathname } = new URL(request.url);

  return CLIENT_SCREEN_PATHS.some((path) => matchPath(path, pathname))
    ? null
    : data<NothingHere>({ notFound: true }, { status: 404 });
}

/** Inside the app the route table below decides on its own. */
export const clientLoader = () => null;

export default function ClientApp() {
  const nothingHere = (useLoaderData() as NothingHere | null)?.notFound;
  const { isLoggedIn } = useSession();

  if (nothingHere) return <NotFound />;

  return (
    <Routes>
      <Route path="/plans" element={<Plans />} />
      <Route path="/register" element={<Register />} />
      {/* The account session opens the deal list and the deals themselves (api#402, step 2): the
          api scopes both verticals to the account's primary wallet and accepts the account token
          on every one of these routes, so a renter on a phone with no wallet sees their own
          rentals and purchases (app#238). Signed out, the screen is the sign-in form, in place —
          the api would answer 401, and the screen would call that an error on our side (app#210). */}
      <Route path="/deals" element={isLoggedIn ? <Deals /> : <Auth />} />
      <Route path="/delivery/:id" element={isLoggedIn ? <DeliveryDeal /> : <Auth />} />
      {/* The renter's email lands here on a phone where no wallet exists (app#236). On-chain
          actions still need the linked wallet, and say so when pressed. */}
      <Route path="/rental/:id" element={isLoggedIn ? <Rental /> : <Auth />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/notifications" element={<Notifications />} />
      {/* XP is the ledger of the account's wallet: read under the account session, which the api
          resolves to that wallet (app#217, api#402). The screen says so to an account without one. */}
      <Route path="/xp-transactions" element={isLoggedIn ? <XpTransactions /> : <Auth />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth-modal" element={<Auth modal />} />
      {/* Everything else: an address with nothing at it says so (SF-A2), instead of a blank
          screen under the tab bar. `routes.spec.ts` keeps this line from disappearing. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
